import { useState, MouseEvent, ChangeEvent, useEffect } from "react";
import { ITablePagination, TableSortOrder } from "../../../Typings/SortableTable";
import { ITableColumn } from "../../../Typings/TableColumn";
import { useLocalStorage } from "react-use";
import { clearOldLocalStorage, TABLE_STATE_VERSION_DATE } from "../../../Utilities/browserStorage";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const bLower =
    typeof b[orderBy] === "string" ? (b[orderBy] as unknown as string).toLowerCase() : b[orderBy];
  const aLower =
    typeof a[orderBy] === "string" ? (a[orderBy] as unknown as string).toLowerCase() : a[orderBy];

  if (bLower < aLower) {
    return -1;
  }
  if (bLower > aLower) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: TableSortOrder,
  orderBy: Key,
): (
  a: { [key in Key]: number | string | Date | any[] },
  b: { [key in Key]: number | string | Date | any[] },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function useSortableTableState(
  tableData: any[],
  columns: any[],
  name: string,
  isLoading: boolean,
  pagination?: ITablePagination,
  defaultOrderBy?: string,
  disableLocalStorage?: boolean,
) {
  const [tableSettings, setTableSettings] = useLocalStorage<ITableColumn[]>(
    `tbl-state-${name}-${TABLE_STATE_VERSION_DATE}`,
  );

  const [viewableColumns, setViewableColumns] = useState<ITableColumn[]>(
    !disableLocalStorage ? (tableSettings ?? []) : [],
  );

  const [editTableColumnsOpen, setEditTableColumnsOpen] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState(tableData);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    pagination?.enabled === true ? pagination.rowsPerPage[0] : 10,
  );
  const [order, setOrder] = useState<TableSortOrder>("desc");
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy ?? "dateAdded");

  const handleRequestSort = (event: MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const viewableCheckboxOnClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    let updatedViewableColumns = viewableColumns ?? columns;

    if (event.target.checked) {
      const column = (columns as ITableColumn[]).find(
        (v) => v.label.toLowerCase() === event.target.value.toLowerCase(),
      );

      if (column) {
        updatedViewableColumns.push(column);
      }
    } else {
      updatedViewableColumns = updatedViewableColumns.filter(
        (v) => v.label.toLowerCase() !== event.target.value.toLowerCase(),
      );
    }

    setViewableColumns(updatedViewableColumns);
    setTableCookie(updatedViewableColumns);
  };

  const setTableCookie = (columns: ITableColumn[]) => {
    if (!disableLocalStorage) {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      setTableSettings(columns);
    }
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

  useEffect(() => {
    setFilteredData(tableData);
    if (!isLoading && columns && (!viewableColumns || viewableColumns.length <= 0)) {
      setViewableColumns(columns);
      setTableCookie(columns);
    }
    // ignore setTableCookie
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, columns, setViewableColumns, isLoading, viewableColumns]);

  return {
    getters: {
      filteredData,
      page,
      rowsPerPage,
      order,
      orderBy,
      emptyRows,
      editTableColumnsOpen,
      viewableColumns,
    },
    setters: {
      setFilteredData,
      setPage,
      setRowsPerPage,
      setOrder,
      setOrderBy,
      setEditTableColumnsOpen,
    },
    handlers: {
      handleRequestSort,
      handleChangePage,
      handleChangeRowsPerPage,
      viewableCheckboxOnClick,
    },
    effects: {
      stableSort,
      getComparator,
    },
  };
}

clearOldLocalStorage("tbl-state-", TABLE_STATE_VERSION_DATE);
